import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, ChevronRight, ChevronLeft, Check, Rocket, 
  FolderPlus, FileUp, Layers, Palette, Calculator,
  ArrowRight, Sparkles, Home, Users, MapPin
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WIZARD_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to KEY-MADE',
    subtitle: 'Your Professional Management Partner',
    icon: Rocket,
    color: '#BC5D41'
  },
  {
    id: 'project',
    title: 'Create Your First Project',
    subtitle: 'Every great design starts here',
    icon: FolderPlus,
    color: '#3B82F6'
  },
  {
    id: 'floorplan',
    title: 'Upload Floor Plans',
    subtitle: 'The foundation for accurate takeoffs',
    icon: FileUp,
    color: '#F59E0B'
  },
  {
    id: 'rooms',
    title: 'Define Rooms',
    subtitle: 'Draw polygons to measure spaces',
    icon: Layers,
    color: '#8B5CF6'
  },
  {
    id: 'selections',
    title: 'Capture Selections',
    subtitle: 'Save products with our Web Clipper',
    icon: Palette,
    color: '#EC4899'
  },
  {
    id: 'estimate',
    title: 'Generate Estimates',
    subtitle: 'Turn selections into proposals',
    icon: Calculator,
    color: '#10B981'
  },
  {
    id: 'complete',
    title: "You're All Set!",
    subtitle: 'Start building your project',
    icon: Check,
    color: '#22C55E'
  }
];

export default function QuickStartWizard({ open, onClose, onProjectCreated,
                                           prefill = null, sourceLead = null }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [createdProject, setCreatedProject] = useState(null);
  
  // Project form data
  const [projectData, setProjectData] = useState({
    name: '',
    client_name: '',
    address: '',
    project_type: 'residential',
    description: ''
  });

  // PL-1 — a lead moved to Signed arrives here with its own answers already in
  // hand. The template step is untouched: that choice stays the user's.
  useEffect(() => {
    if (!open || !prefill) return;
    setCurrentStep(0);
    setCreatedProject(null);
    setProjectData((current) => ({ ...current, ...prefill }));
  }, [open, prefill]);

  const step = WIZARD_STEPS[currentStep];
  const progress = ((currentStep) / (WIZARD_STEPS.length - 1)) * 100;

  const handleNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateProject = async () => {
    if (!projectData.name) {
      toast.error('Please enter a project name');
      return;
    }
    
    setIsCreating(true);
    try {
      // 1. Create the project
      const response = await axios.post(`${API}/projects`, {
        ...projectData,
        ...(sourceLead?.id ? { lead_id: sourceLead.id, crm_lead_id: sourceLead.id } : {}),
        status: 'active',
        phases: [
          { id: 'programming', name: 'Programming', status: 'not_started' },
          { id: 'design', name: 'Design Development', status: 'not_started' },
          { id: 'documentation', name: 'Documentation', status: 'not_started' },
          { id: 'construction', name: 'Construction', status: 'not_started' }
        ]
      });
      
      // 2. A project made from a lead is already in the pipeline — it is
      // linked to the lead it came from, and no second lead is invented.
      if (sourceLead?.id) {
        try {
          await axios.post(`${API}/crm/leads/${sourceLead.id}/link-project`, {
            project_id: response.data.id,
          });
        } catch (e) {
          toast.error('Project created, but the lead could not be linked to it');
        }
        setCreatedProject(response.data);
        toast.success('Project created successfully!');
        onProjectCreated?.(response.data);
        handleNext();
        return;
      }

      // 3. Otherwise: a project started from scratch gets its own pipeline card
      try {
        await axios.post(`${API}/crm/leads`, {
          name: projectData.client_name || projectData.name,
          email: '',
          phone: '',
          company: '',
          source: 'Quick Start',
          project_type: projectData.project_type || 'residential',
          budget_range: '',
          stage: 'won',
          notes: projectData.description || '',
          property_address: projectData.address || '',
          converted_project_id: response.data.id
        });
      } catch (e) {
        console.warn('CRM lead creation skipped:', e);
      }
      
      setCreatedProject(response.data);
      toast.success('Project created successfully!');
      onProjectCreated?.(response.data);
      handleNext();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  const handleGoToProject = () => {
    if (createdProject) {
      onClose();
      navigate(`/projects/${createdProject.id}`);
    }
  };

  const handleGoToFloorPlan = () => {
    if (createdProject) {
      onClose();
      navigate(`/projects/${createdProject.id}/floor-plans`);
    }
  };

  const handleGoToClips = () => {
    if (createdProject) {
      onClose();
      navigate(`/projects/${createdProject.id}/clips`);
    }
  };

  const handleSkip = () => {
    onClose();
    toast.info('You can access the Quick Start anytime from My Desk');
  };

  const renderStepContent = () => {
    switch (step.id) {
      case 'welcome':
        return (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#70170F] to-[#BC5D41] mb-6">
              <Rocket className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Welcome to KEY-MADE</h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Your digital partner for managing architectural and interior design projects 
              with precision, intelligence, and intention.
            </p>
            
            <div className="grid grid-cols-3 gap-4 mt-8 text-left">
              <div className="p-4 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mb-3">
                  <Home className="w-5 h-5 text-blue-400" />
                </div>
                <h4 className="text-white font-medium text-sm mb-1">Design Tools</h4>
                <p className="text-gray-500 text-xs">Floor plans, selections, mood boards</p>
              </div>
              <div className="p-4 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center mb-3">
                  <Calculator className="w-5 h-5 text-green-400" />
                </div>
                <h4 className="text-white font-medium text-sm mb-1">21+ Calculators</h4>
                <p className="text-gray-500 text-xs">Pre-engineering made easy</p>
              </div>
              <div className="p-4 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
                <h4 className="text-white font-medium text-sm mb-1">Client Portal</h4>
                <p className="text-gray-500 text-xs">The Drawing Room</p>
              </div>
            </div>
            
            <p className="text-[#BC5D41] text-sm mt-8 font-medium">
              Let&apos;s set up your first project in under 2 minutes
            </p>
          </div>
        );

      case 'project':
        return (
          <div className="py-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <FolderPlus className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Create Your Project</h3>
                <p className="text-gray-500 text-sm">This unlocks all project-specific tools</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Project Name *</Label>
                <Input
                  placeholder="e.g., Smith Residence Kitchen Remodel"
                  value={projectData.name}
                  onChange={(e) => setProjectData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Client Name</Label>
                <Input
                  placeholder="e.g., John & Jane Smith"
                  value={projectData.client_name}
                  onChange={(e) => setProjectData(prev => ({ ...prev, client_name: e.target.value }))}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Project Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    placeholder="123 Main St, City, State"
                    value={projectData.address}
                    onChange={(e) => setProjectData(prev => ({ ...prev, address: e.target.value }))}
                    className="mt-1 pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Brief Description</Label>
                <Textarea
                  placeholder="What's the scope of this project?"
                  value={projectData.description}
                  onChange={(e) => setProjectData(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1 bg-[#1A1A1A] border-[#2A2A2A] text-white"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="mt-6 p-3 rounded-lg bg-[#BC5D41]/10 border border-[#BC5D41]/30">
              <p className="text-[#BC5D41] text-xs flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Tip: You can always edit these details later from Project Settings
              </p>
            </div>
          </div>
        );

      case 'floorplan':
        return (
          <div className="py-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <FileUp className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Upload Floor Plans</h3>
                <p className="text-gray-500 text-sm">PDFs work best for accurate scaling</p>
              </div>
            </div>
            
            <div className="bg-[#1A1A1A] border-2 border-dashed border-[#2A2A2A] rounded-xl p-8 text-center">
              <FileUp className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">Upload your floor plan PDF</p>
              <p className="text-gray-600 text-sm mb-4">Or skip this step and add later</p>
              
              {createdProject ? (
                <Button 
                  onClick={handleGoToFloorPlan}
                  className="bg-[#70170F] hover:bg-[#70170F]/90"
                >
                  Go to Floor Plans
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <p className="text-yellow-500 text-sm">Create a project first to upload floor plans</p>
              )}
            </div>
            
            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              <div className="p-3 rounded-lg bg-[#1A1A1A]">
                <p className="text-2xl font-bold text-white">1</p>
                <p className="text-xs text-gray-500">Upload PDF</p>
              </div>
              <div className="p-3 rounded-lg bg-[#1A1A1A]">
                <p className="text-2xl font-bold text-white">2</p>
                <p className="text-xs text-gray-500">Set Scale</p>
              </div>
              <div className="p-3 rounded-lg bg-[#1A1A1A]">
                <p className="text-2xl font-bold text-white">3</p>
                <p className="text-xs text-gray-500">Draw Rooms</p>
              </div>
            </div>
          </div>
        );

      case 'rooms':
        return (
          <div className="py-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Layers className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Define Rooms</h3>
                <p className="text-gray-500 text-sm">Draw polygons to measure spaces accurately</p>
              </div>
            </div>
            
            <div className="bg-[#1A1A1A] rounded-xl p-6">
              <div className="aspect-video bg-[#0D0D0D] rounded-lg flex items-center justify-center mb-4 border border-[#2A2A2A]">
                <div className="text-center">
                  <Layers className="w-16 h-16 text-gray-700 mx-auto mb-2" />
                  <p className="text-gray-600 text-sm">Room polygon drawing tool</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-gray-300">Auto-calculate square footage</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-gray-300">Assign rooms to floor levels</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-gray-300">Link selections to specific rooms</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'selections':
        return (
          <div className="py-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center">
                <Palette className="w-6 h-6 text-pink-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Capture Selections</h3>
                <p className="text-gray-500 text-sm">Save products from anywhere on the web</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="w-10 h-10 rounded-lg bg-[#BC5D41]/20 flex items-center justify-center mb-3">
                  <Sparkles className="w-5 h-5 text-[#BC5D41]" />
                </div>
                <h4 className="text-white font-medium text-sm mb-1">Web Clipper</h4>
                <p className="text-gray-500 text-xs">Chrome extension captures product data automatically</p>
              </div>
              <div className="p-4 rounded-xl bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mb-3">
                  <FolderPlus className="w-5 h-5 text-blue-400" />
                </div>
                <h4 className="text-white font-medium text-sm mb-1">Manual Entry</h4>
                <p className="text-gray-500 text-xs">Add products directly in the Clips Library</p>
              </div>
            </div>
            
            {createdProject && (
              <Button 
                onClick={handleGoToClips}
                variant="outline"
                className="w-full border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
              >
                Open Clips Library
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            
            <div className="mt-6 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
              <p className="text-green-400 text-xs flex items-center gap-2">
                <Check className="w-4 h-4" />
                Selections flow into estimates, bid packages, and client approvals automatically
              </p>
            </div>
          </div>
        );

      case 'estimate':
        return (
          <div className="py-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Calculator className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Generate Estimates</h3>
                <p className="text-gray-500 text-sm">Turn room data and selections into proposals</p>
              </div>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="p-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] flex items-center gap-4">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <div>
                  <p className="text-white text-sm font-medium">Programming Estimate</p>
                  <p className="text-gray-500 text-xs">Quick ballpark for early discussions</p>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] flex items-center gap-4">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div>
                  <p className="text-white text-sm font-medium">Design Estimate</p>
                  <p className="text-gray-500 text-xs">Room-by-room with allowances</p>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] flex items-center gap-4">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <div>
                  <p className="text-white text-sm font-medium">Detailed Estimate</p>
                  <p className="text-gray-500 text-xs">Line-item breakdown for contracts</p>
                </div>
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-[#BC5D41]/10 border border-[#BC5D41]/30">
              <p className="text-[#BC5D41] text-xs">
                <strong>Pro tip:</strong> Use the 21+ pre-engineering calculators to get accurate quantities before estimating
              </p>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">You&apos;re All Set!</h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              {createdProject 
                ? `Your project "${createdProject.name}" is ready. Start designing!`
                : "You're ready to start using KEY-MADE. Create your first project whenever you're ready."
              }
            </p>
            
            <div className="space-y-3">
              {createdProject && (
                <Button 
                  onClick={handleGoToProject}
                  className="w-full bg-[#70170F] hover:bg-[#70170F]/90"
                  size="lg"
                >
                  Open Project Hub
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
              <Button 
                onClick={onClose}
                variant="outline"
                className="w-full border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
              >
                Go to My Desk
              </Button>
            </div>
            
            <p className="text-gray-600 text-xs mt-6">
              Need help? Check the System Blueprint for a complete feature guide.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl bg-[#0D0D0D] border-[#1A1A1A] text-white p-0 gap-0 overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-[#1A1A1A]">
          <div 
            className="h-full bg-gradient-to-r from-[#70170F] to-[#BC5D41] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Header */}
        <DialogHeader className="p-4 pb-0 flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: step.color + '20' }}
            >
              <step.icon className="w-4 h-4" style={{ color: step.color }} />
            </div>
            <div>
              <DialogTitle className="text-sm font-medium">{step.title}</DialogTitle>
              <p className="text-xs text-gray-500">{step.subtitle}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleSkip}
            className="text-gray-500 hover:text-white"
          >
            <X className="w-4 h-4" />
          </Button>
        </DialogHeader>
        
        {/* Content */}
        <div className="p-6">
          {renderStepContent()}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-[#1A1A1A] flex items-center justify-between bg-[#0A0A0A]">
          <div className="flex items-center gap-2">
            {WIZARD_STEPS.map((s, i) => (
              <div 
                key={s.id}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === currentStep ? 'bg-[#BC5D41]' : 
                  i < currentStep ? 'bg-green-500' : 'bg-[#2A2A2A]'
                }`}
              />
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            {currentStep > 0 && currentStep < WIZARD_STEPS.length - 1 && (
              <Button 
                variant="ghost" 
                onClick={handleBack}
                className="text-gray-400 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            
            {step.id === 'project' ? (
              <Button 
                onClick={handleCreateProject}
                disabled={isCreating || !projectData.name}
                className="bg-[#70170F] hover:bg-[#70170F]/90"
              >
                {isCreating ? 'Creating...' : 'Create Project'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : currentStep < WIZARD_STEPS.length - 1 ? (
              <Button 
                onClick={handleNext}
                className="bg-[#70170F] hover:bg-[#70170F]/90"
              >
                {currentStep === 0 ? "Let's Go" : 'Next'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
